/* useCapabilityIndex — capability id → "capability name · source name"
 * resolution for the workflow flow graph. Fetches the source list + each
 * source doc ONCE per org/app (module-level promise cache shared across
 * mounts) and returns a Map; null while loading. Failures resolve to an
 * empty map so the graph falls back to raw capability ids. */
import {
    useEffect,
    useState
} from 'react';
import {
    appEngineService
} from '../../api/appEngine.service';
import type {
    SourceDoc,
    Tenancy
} from '../../api/types';
import type {
    CapInfo
} from './workflowGraphTypes';

export type CapIndex = Map<string, CapInfo>;

const cache = new Map<string, Promise<CapIndex>>();

async function load(t: Tenancy): Promise<CapIndex> {
    const idx: CapIndex = new Map();
    const items = await appEngineService.listDefs(t, 'source');
    const docs = await Promise.all(items.map((s) => appEngineService
        .getDef(t, 'source', s.id).catch(() => null)));
    docs.forEach((d: SourceDoc | null, i) => {
        if (!d) return;
        const sourceName = d.source?.name || items[i].name || items[i].id;
        for (const c of d.capabilities || []) {
            if (!c?.id) continue;
            idx.set(c.id, {
                capName: c.name || c.id,
                sourceName
            });
        }
    });
    return idx;
}

export default function useCapabilityIndex(t: Tenancy): CapIndex | null {
    const [idx, setIdx] = useState<CapIndex | null>(null);
    useEffect(() => {
        let live = true;
        const key = `${t.org_id}/${t.app_id}`;
        let p = cache.get(key);
        if (!p) {
            p = load(t);
            cache.set(key, p);
            p.catch(() => cache.delete(key)); // allow a retry next mount
        }
        p.then((m) => {
            if (live) setIdx(m);
        }).catch(() => {
            if (live) setIdx(new Map());
        });
        return () => {
            live = false;
        };
    }, [t]);
    return idx;
}
